import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {ImageTextureExtension, ImageTextureInterpolation, ImageTextureProjection} from "#material/nodes/texture/tex-image/types"
import {
    imageTextureExtensionSchema,
    imageTextureImageColorspaceSettingsNameSchema,
    imageTextureInterpolation,
    imageTextureProjection,
} from "#material/nodes/texture/tex-image/types"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"
import type {TypedImageData} from "@cm/utils"
import {typedImageData} from "@cm/utils/typed-image-data"

export interface TexImageSockets extends MaterialNodeSockets {
    inputs: {
        vector: "point" // found in inputs: point,color; found in settings: vector,input; inferred from data: float3,null
    }
    settings: {
        alpha: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        colorspace: "string"
        dataObjectId: "string"
        dataObjectLegacyId: "int"
        textureSetRevisionId: "string" // this assigned data-object is part of this texture set revision
        imageColorspaceSettingsName: "string" // found in inputs: -; found in settings: string; inferred from data: string
        imageFilepath: "string" // found in inputs: -; found in settings: string; inferred from data: string
        disableProgressiveLoading: "boolean"
        forceOriginalResolution: "boolean"
        projectionBlend: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point,color; found in settings: vector,input; inferred from data: float3,null
    }
    settingsExtensions: {
        extension?: ImageTextureExtension // found in inputs: -; found in settings: string; inferred from data: string
        interpolation?: ImageTextureInterpolation // found in inputs: -; found in settings: string; inferred from data: string
        projection?: ImageTextureProjection // found in inputs: -; found in settings: string; inferred from data: string
        inMemoryImageData?: TypedImageData
    }
    outputs: {
        color: "color"
        alpha: "float"
    }
}

export const texImageDefinition = {
    exchangeFormat: {
        nodeName: null,
    },
    nodeClass: "TexImage",
    ui: {
        label: "Tex Image",
        color: "#9e6034",
        description: "Low-level node corresponding to blender's TexImage. Use mapping node to scale image to [0,1].",
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "alpha", valueType: "float", label: "Alpha", output: true},
            {name: "vector", valueType: "point", label: "Vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            // TODO: add more settings
            {
                name: "interpolation",
                defaultValue: "Linear",
                label: "Interpolation",
                valueType: "string",
                options: [
                    {label: "Linear", value: "Linear"},
                    {label: "Closest", value: "Closest"},
                    {label: "Cubic", value: "Cubic"},
                    {label: "Smart", value: "Smart"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "projection",
                defaultValue: "FLAT",
                label: "Projection",
                valueType: "string",
                options: [
                    {label: "Flat", value: "FLAT"},
                    {label: "Box", value: "BOX"},
                    {label: "Sphere", value: "SPHERE"},
                    {label: "Tube", value: "TUBE"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "extension",
                defaultValue: "REPEAT",
                label: "Extension",
                valueType: "string",
                options: [
                    {label: "Repeat", value: "REPEAT"},
                    {label: "Extend", value: "EXTEND"},
                    {label: "Clip", value: "CLIP"},
                    {label: "Mirror", value: "MIRROR"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: {full: false, remark: "Some filters/extension modes unsupported"},
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, alpha: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                vector: optionalMaterialInput,
            }),
            settings: z.strictObject({
                alpha: z.optional(z.number()),
                color: z.optional(color),
                ...keepLegacyColorMapping,
                colorspace: z.optional(z.string()),
                dataObjectId: z.optional(z.string()),
                dataObjectLegacyId: z.optional(z.number()),
                textureSetRevisionId: z.optional(z.string()),
                imageFilepath: z.optional(z.string()),
                disableProgressiveLoading: z.optional(z.boolean()),
                extension: z.optional(imageTextureExtensionSchema),
                forceOriginalResolution: z.optional(z.boolean()),
                imageColorspaceSettingsName: z.optional(imageTextureImageColorspaceSettingsNameSchema),
                interpolation: z.optional(imageTextureInterpolation),
                projection: z.optional(imageTextureProjection),
                projectionBlend: z.optional(z.number()),
                inMemoryImageData: z.optional(typedImageData),
                ...keepLegacyTextureMapping,
                vector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexImageSockets>
